import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sun, Moon, Menu, X, Linkedin, Twitter, Github, ArrowRight } from "lucide-react";
import DemoButton from "./DemoButton";
import Logo from "@/components/Logo";

const SiteCtx = createContext({ site: null, theme: "dark" });
export const useSite = () => useContext(SiteCtx);

export function useSeo({ title, description }) {
  useEffect(() => {
    const full = title ? `${title} · CyboScan` : "CyboScan — AI-Powered Cybersecurity for Every Endpoint";
    document.title = full;
    const set = (sel, attr, val) => { let el = document.querySelector(sel); if (!el) { el = document.createElement("meta"); const [k, v] = sel.replace(/^meta\[|\]$/g, "").split("="); el.setAttribute(k, v.replace(/"/g, "")); document.head.appendChild(el); } el.setAttribute(attr, val); };
    if (description) { set('meta[name="description"]', "content", description); set('meta[property="og:description"]', "content", description); }
    set('meta[property="og:title"]', "content", full);
    set('meta[property="og:url"]', "content", `https://www.cyboscan.com${window.location.pathname}`);
    let c = document.querySelector('link[rel="canonical"]'); if (c) c.setAttribute("href", `https://www.cyboscan.com${window.location.pathname}`);
  }, [title, description]);
}

const NAV = [["Product", "/product"], ["Solutions", "/solutions"], ["Pricing", "/pricing"], ["Resources", "/resources"], ["About", "/about"], ["Contact", "/contact"]];

export default function PublicLayout() {
  const [theme, setTheme] = useState(() => localStorage.getItem("cybo_theme") || "dark");
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: () => api.get("/site").then((r) => r.data), staleTime: 60000 });
  useEffect(() => { localStorage.setItem("cybo_theme", theme); }, [theme]);
  useEffect(() => { setOpen(false); window.scrollTo({ top: 0 }); }, [pathname]);

  return (
    <SiteCtx.Provider value={{ site, theme }}>
      <div className={`site min-h-screen ${theme === "light" ? "light" : ""}`}>
        <header data-testid="site-header" className="sticky top-0 z-40 s-glass border-b s-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
            <Link to="/" data-testid="site-logo" className="flex items-center"><Logo size={34} light={theme === "light"} id="hdr" /></Link>
            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map(([l, to]) => (
                <NavLink key={to} to={to} data-testid={`nav-${l.toLowerCase()}`} className={({ isActive }) => `px-3 py-2 text-sm rounded-sm transition-colors duration-150 ${isActive ? "s-primary" : "s-text2 hover:text-[var(--text)]"}`}>{l}</NavLink>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-2">
              <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Toggle theme" className="p-2 s-text2 hover:text-[var(--text)] transition-colors duration-150">{theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}</button>
              <Link to="/customer/login" data-testid="header-customer-login" className="px-3 py-2 text-sm s-text2 hover:text-[var(--text)] transition-colors duration-150">Customer Login</Link>
              <Link to="/admin/login" data-testid="header-admin-login" className="px-3 py-2 text-sm s-text2 hover:text-[var(--text)] transition-colors duration-150">Admin Login</Link>
              <DemoButton className="btn-ghost px-3 py-2 text-sm font-semibold" testid="header-try-demo" />
              <Link to="/customer/register" data-testid="header-start-trial" className="btn-primary px-4 py-2 text-sm font-semibold flex items-center gap-1.5">Get Started <ArrowRight size={14} /></Link>
            </div>
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2" aria-label="Menu">{open ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
          {open && (
            <div data-testid="mobile-menu" className="lg:hidden border-t s-border s-surface px-4 py-4 space-y-1">
              {NAV.map(([l, to]) => <NavLink key={to} to={to} className="block px-3 py-2.5 text-sm">{l}</NavLink>)}
              <div className="border-t s-border pt-3 mt-2 grid gap-2">
                <Link to="/customer/login" className="btn-ghost px-3 py-2.5 text-sm text-center">Customer Login</Link>
                <Link to="/admin/login" className="btn-ghost px-3 py-2.5 text-sm text-center">Admin Login</Link>
                <DemoButton className="btn-ghost px-3 py-2.5 text-sm w-full" testid="mobile-try-demo" />
                <Link to="/customer/register" className="btn-primary px-3 py-2.5 text-sm text-center font-semibold">Get Started</Link>
                <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="text-xs s-text2 py-2">Switch to {theme === "dark" ? "light" : "dark"} mode</button>
              </div>
            </div>
          )}
        </header>
        <main><Outlet /></main>
        <Footer site={site} />
      </div>
    </SiteCtx.Provider>
  );
}

const COLS = [
  ["Product", [["Endpoint Discovery", "/product#discovery"], ["Endpoint Security", "/product#security"], ["AI Security", "/product#ai"], ["Pricing", "/pricing"]]],
  ["Solutions", [["Small Business", "/solutions"], ["MSP / MSSP", "/solutions"], ["Enterprise", "/solutions"], ["Compliance", "/solutions"]]],
  ["Resources", [["Documentation", "/resources"], ["Agent Installer", "/resources#agent"], ["Brand kit", "/brand/cyboscan-brand-kit.zip"], ["System status", "/status"], ["Support", "/contact"]]],
  ["Legal", [["Privacy Policy", "/legal/privacy"], ["Terms of Service", "/legal/terms"], ["Cookie Policy", "/legal/cookies"], ["Acceptable Use", "/legal/acceptable-use"], ["Security", "/legal/security"]]],
];

function Footer({ site }) {
  return (
    <footer data-testid="site-footer" className="border-t s-border s-surface mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <Logo size={36} sub="AI-powered cybersecurity" id="ftr" />
          <p className="text-sm s-text2 mt-4 max-w-xs leading-relaxed">AI-powered cybersecurity for every endpoint. Discover, monitor, protect and respond — from one dashboard.</p>
          <div className="flex gap-3 mt-5 s-text2">
            {site?.social?.linkedin && <a href={site.social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-[var(--text)]"><Linkedin size={16} /></a>}
            {site?.social?.twitter && <a href={site.social.twitter} target="_blank" rel="noreferrer" aria-label="X" className="hover:text-[var(--text)]"><Twitter size={16} /></a>}
            {site?.social?.github && <a href={site.social.github} target="_blank" rel="noreferrer" aria-label="GitHub" className="hover:text-[var(--text)]"><Github size={16} /></a>}
          </div>
        </div>
        {COLS.map(([h, links]) => (
          <div key={h}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] s-text2 mb-3">{h}</div>
            <ul className="space-y-2">{links.map(([l, to]) => <li key={l}>{to.endsWith(".zip") ? <a href={to} download data-testid="footer-brand-kit" className="text-sm s-text2 hover:text-[var(--text)] transition-colors duration-150">{l}</a> : <Link to={to} className="text-sm s-text2 hover:text-[var(--text)] transition-colors duration-150">{l}</Link>}</li>)}</ul>
          </div>
        ))}
      </div>
      <div className="border-t s-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs s-text2 font-mono">
          <span>© {new Date().getFullYear()} CyboScan · {site?.address || ""}</span>
          <span>{site?.support_email}</span>
        </div>
      </div>
    </footer>
  );
}

export const Section = ({ kicker, title, sub, children, className = "", id }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 ${className}`}>
    {(kicker || title) && (
      <div className="max-w-2xl mb-12">
        {kicker && <div className="font-mono text-xs uppercase tracking-[0.2em] s-primary mb-3">{kicker}</div>}
        {title && <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{title}</h2>}
        {sub && <p className="s-text2 mt-4 text-base leading-relaxed">{sub}</p>}
      </div>
    )}
    {children}
  </section>
);

export const CTA = ({ title = "Protect Your Business Today", sub = "Simple per-endpoint pricing, billed annually. Create your account in two minutes or book a demo with our security engineers." }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
    <div className="s-elev border s-border p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8 glow-beam">
      <div><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h2><p className="s-text2 mt-3 max-w-xl">{sub}</p></div>
      <div className="flex flex-wrap gap-3">
        <Link to="/customer/register" data-testid="cta-start-trial" className="btn-primary px-6 py-3 font-semibold flex items-center gap-2">Get Started <ArrowRight size={16} /></Link>
        <Link to="/contact?type=demo" data-testid="cta-book-demo" className="btn-ghost px-6 py-3 font-semibold">Book a Demo</Link>
      </div>
    </div>
  </section>
);
